/**
 * Whose bridges the bridge screens manage: the account's own (the default), or the master
 * account's in the board. The screens and their hooks read their paths and refresh from here.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useMasterAccountBridgesEnabled } from '../hooks/use-master-account-bridges-enabled';
import { navigation } from '@/navigation';

export interface BridgeScope {
  /** The master account's bridges, in master context: no push, and no account installations. */
  office: boolean;
  listPath: string;
  newPath: string;
  detailPath: (id: string) => string;
  /** Whether a bridge may be registered; the office's only where the server enables it. */
  canRegister: boolean;
}

export const ACCOUNT_BRIDGE_SCOPE: BridgeScope = {
  office: false,
  listPath: navigation.integrationBridges(),
  newPath: navigation.integrationBridgeNew(),
  detailPath: navigation.integrationBridge,
  canRegister: true
};

const OFFICE_PATHS: Omit<BridgeScope, 'canRegister'> = {
  office: true,
  listPath: navigation.masterAccountBridges(),
  newPath: navigation.masterAccountBridgeNew(),
  detailPath: navigation.masterAccountBridge
};

const BridgeScopeContext = createContext<BridgeScope>(ACCOUNT_BRIDGE_SCOPE);

/** Puts the screens below it on the master account's bridges. */
export function OfficeBridgeScope({ children }: { children: ReactNode }) {
  // Not a kill switch: with registering off the office still manages the bridges it has.
  const { enabled: canRegister } = useMasterAccountBridgesEnabled();
  const scope = useMemo(() => ({ ...OFFICE_PATHS, canRegister }), [canRegister]);
  return <BridgeScopeContext.Provider value={scope}>{children}</BridgeScopeContext.Provider>;
}

export function useBridgeScope(): BridgeScope {
  return useContext(BridgeScopeContext);
}
